import { useAuth0 } from "@auth0/auth0-react";
import { Building2, LogOut, Newspaper } from "lucide-react";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../../hooks/use-user";

export type SuperAdminAbschnitt = "organisationen" | "neuigkeiten";

interface Punkt {
  abschnitt: SuperAdminAbschnitt;
  label: string;
  pfad: string;
  icon: ReactNode;
}

const PUNKTE: Punkt[] = [
  {
    abschnitt: "organisationen",
    label: "Organisationen",
    pfad: "/super-admin",
    icon: <Building2 size={18} strokeWidth={1.8} aria-hidden="true" />,
  },
  {
    // Dasselbe Zeichen, das der Kunde fuer seine Neuigkeiten sieht.
    abschnitt: "neuigkeiten",
    label: "Neuigkeiten",
    pfad: "/super-admin/neuigkeiten",
    icon: <Newspaper size={18} strokeWidth={1.8} aria-hidden="true" />,
  },
];

/**
 * Der Rahmen des Super-Admin-Bereichs (KAN-276, A8).
 *
 * Bis hierher war der Bereich eine einzelne Organisationsliste. Mit dieser Navigation wird
 * er ein Bereich: Die Liste bekommt ihren Platz als erster Punkt, die Neuigkeiten kommen
 * daneben. Weitere Punkte haengen sich hier ein, ohne die Organisationsliste anzufassen.
 */
export const SuperAdminBereich = ({ aktiv, children }: { aktiv: SuperAdminAbschnitt; children: ReactNode }) => (
  <div className="flex min-h-screen flex-wrap items-start">
    {/* Bleibt stehen, waehrend die Liste scrollt — sonst waere der Weg aus dem Bereich
        heraus auf langen Seiten nur ganz unten erreichbar. */}
    <nav
      aria-label="Super-Admin-Bereiche"
      className="sticky top-0 flex h-screen min-w-[196px] max-w-[262px] flex-1 flex-col gap-1 overflow-y-auto border-r border-gray-200 bg-white px-3 py-5"
      style={{ flexBasis: "230px" }}
    >
      <Link to="/super-admin" className="mb-6 block px-2.5">
        <img src="/kandidat-logo.png" alt="kandidat.io" className="h-7 w-auto" />
      </Link>

      <div className="px-2.5 pb-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Verwaltung</div>
      {PUNKTE.map(punkt =>
        punkt.abschnitt === aktiv ? (
          <span
            key={punkt.abschnitt}
            aria-current="page"
            className="flex items-center gap-2.5 rounded-lg bg-neuigkeit-hell px-2.5 py-2 text-sm font-medium text-neuigkeit-dunkel"
          >
            {punkt.icon}
            {punkt.label}
          </span>
        ) : (
          <Link
            key={punkt.abschnitt}
            to={punkt.pfad}
            className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {punkt.icon}
            {punkt.label}
          </Link>
        )
      )}
      <KontoBlock />
    </nav>

    <main className="min-w-0 flex-[999_1_620px] px-6 pb-10 pt-6">{children}</main>
  </div>
);

/**
 * Konto und Abmelden am Fuss der Spalte — an derselben Stelle, an der beides vorher in der
 * blauen Leiste sass. Ohne diesen Block gaebe es im Bereich keinen Weg hinaus.
 */
const KontoBlock = () => {
  const { user } = useUser();
  const { logout } = useAuth0();

  return (
    <div className="mt-auto border-t border-gray-100 pt-3">
      <div className="px-2.5 pb-1.5">
        <div className="truncate text-sm font-medium text-gray-900">
          {user?.firstName} {user?.lastName}
        </div>
        <div className="truncate text-xs text-gray-500">{user?.organizationName}</div>
      </div>
      {/* Eigene Linie: Wer angemeldet ist und das Abmelden sind zwei Dinge, nicht eins. */}
      <div className="mt-1.5 border-t border-gray-100 pt-1.5">
        <button
          type="button"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <LogOut size={18} strokeWidth={1.8} />
          Abmelden
        </button>
      </div>
    </div>
  );
};

export default SuperAdminBereich;
